import { memo, useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface Props {
  isOpen: boolean;
  error: string | null;
  onClose: () => void;
  onPlay: (input: string) => void;
}

const CustomStationModal = memo<Props>(({ isOpen, error, onClose, onPlay }) => {
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setInput("");
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [isOpen]);

  const handlePlay = useCallback(() => {
    if (input.trim()) onPlay(input.trim());
  }, [input, onPlay]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handlePlay();
      if (e.key === "Escape") onClose();
    },
    [handlePlay, onClose],
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="absolute inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(6, 9, 14, 0.82)" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            className="w-full max-w-[560px] px-8 flex flex-col items-center gap-5"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            {/* Heading */}
            <div className="text-center">
              <h2 className="text-white text-[26px] font-[400] leading-tight tracking-wide">
                Radio Stations
              </h2>
              <p className="text-white/50 text-[14px] mt-1">
                Huge list of cool stations
              </p>
            </div>

            {/* Input */}
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Paste Something"
              className="w-full bg-white/[0.07] border border-white/[0.08] rounded-md px-5 py-[18px] text-white text-[15px] placeholder-white/25 outline-none focus:border-[#137FE3]/40 focus:bg-white/[0.09] transition-colors"
            />

            {/* Error */}
            <AnimatePresence>
              {error && (
                <motion.p
                  className="text-[#FF5050] text-[13px] text-center -mt-2"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  {error}
                </motion.p>
              )}
            </AnimatePresence>

            {/* Play button */}
            <button
              onClick={handlePlay}
              disabled={!input.trim()}
              className="w-full py-[18px] bg-[#137FE3] hover:bg-[#1a8ff5] border border-[#137FE3] text-white text-[16px] font-[400] rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98]"
            >
              Play Music
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

CustomStationModal.displayName = "CustomStationModal";
export default CustomStationModal;
